import { Button, FormControl, FormHelperText, FormLabel, Input, Stack } from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { FloatingInput } from '@/components/form-elements/FloatingInput';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { EditModalDialog } from '@/components/signs/modal/EditModalDialog';
import { I18N_DE } from '@/i18n/de';
import { CreateSignSupportDto, SignSupportType } from '@/modules/api';
import { backendApiSignSupportsCreateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useCreateSignStore } from '@/stores/useCreateSignStore';
import { CreateSignStep } from '@/types/CreateSignStep';

export const Route = createFileRoute('/_authenticated/_management/verkehrszeichen/$adminLevelId/hinzufuegen')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: CreateSignSupportRoute,
});

type FormData = Pick<CreateSignSupportDto, 'description' | 'type'>;

function CreateSignSupportRoute() {
  const { adminLevelId } = Route.useParams();
  const navigate = Route.useNavigate();
  const formId = useId();
  const geometry = useCreateSignStore((state) => state.geometry);

  const { resetStore, setStep } = useCreateSignStore((state) => state.actions);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: { type: SignSupportType.UNKNOWN } });

  const { mutate, error, isPending } = useMutation({
    ...backendApiSignSupportsCreateMutation(),
    onSuccess: async ({ id }) => {
      setStep(CreateSignStep.GROUP);
      navigate({
        to: '/verkehrszeichen/$adminLevelId/$signSupportId/hinzufuegen',
        params: { adminLevelId, signSupportId: id },
      });
    },
  });

  return (
    <EditModalDialog
      title="Aufstellvorrichtung erstellen"
      onClose={() => {
        resetStore();
        navigate({
          to: '/verkehrszeichen/$adminLevelId',
          params: { adminLevelId },
        });
      }}
    >
      <Stack
        component="form"
        id={formId}
        onSubmit={handleSubmit(({ type, description }) => {
          if (isPending) return;

          mutate({
            body: {
              adminLevelId,
              type,
              description,
              geometry: geometry!,
            },
          });
        })}
        gap={2}
      >
        <FormControl error={!!errors.type}>
          <FormLabel>Typ</FormLabel>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <RadioWithDescriptionGroup
                {...field}
                orientation="horizontal"
                options={Object.values(SignSupportType)}
                optionLabel={({ option }) => I18N_DE.SignSupportType[option]}
              />
            )}
          />
        </FormControl>

        <FormControl error={!!errors.description}>
          <FormLabel>Beschreibung</FormLabel>
          <Input
            type="text"
            variant="outlined"
            slots={{ input: FloatingInput }}
            slotProps={{
              input: {
                ...register('description'),
                label: 'optional',
              },
            }}
          />
        </FormControl>

        {error && <FormHelperText>{error.message}</FormHelperText>}

        <Stack direction="row" gap={2}>
          <Button type="submit" form={formId} disabled={!geometry || isPending} loading={isPending}>
            Speichern
          </Button>
          <Button
            variant="plain"
            onClick={() => {
              resetStore();
              navigate({
                to: '/verkehrszeichen/$adminLevelId',
                params: { adminLevelId },
              });
            }}
          >
            Abbrechen
          </Button>
        </Stack>
      </Stack>
    </EditModalDialog>
  );
}
